import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../lib/roles";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const fmtDate = (d, h) => (d ? `${d.split("-").reverse().join("/")}${h ? " à " + h : ""}` : "—");

export default function InspectionReport() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/inspections/${inspectionId}`).then(({ data }) => setData(data)).catch((err) => {
      setErrorMsg(err.response?.status === 403 ? "Accès refusé — cette inspection appartient à un autre site." : "Inspection introuvable.");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [inspectionId]);

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      await api.post(`/inspections/${inspectionId}/unlock`);
      toast.success("Inspection déverrouillée. Vous pouvez corriger les champs puis enregistrer.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du déverrouillage");
    } finally {
      setUnlocking(false);
    }
  };

  const startEdit = () => setEditForm({
    commentaire: data.inspection.Commentaires_Generaux || "",
    numero_tir: data.inspection.Numero_Tir || "",
    personne_informee: data.inspection.Personne_Informee || "",
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/inspections/${inspectionId}`, editForm);
      toast.success("Inspection corrigée et re-verrouillée.");
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement du rapport...</div>;
  }
  if (!data) {
    return (
      <div data-testid="inspection-report-error" className="min-h-[60vh] flex items-center justify-center text-red-400 font-mono uppercase text-sm text-center px-4">
        {errorMsg}
      </div>
    );
  }

  const { inspection: i, front, reponses, anomalies } = data;
  const sections = [...new Set(reponses.map((r) => r.Section))];
  const isLocked = i.Verrouillee === "TRUE";
  const isAdmin = hasRole(user, "ADMINISTRATEUR");

  return (
    <div data-testid="inspection-report-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <Button data-testid="inspection-report-back" variant="outline" onClick={() => navigate(`/front/${front.Front_ID}?tab=inspections`)} className="border-slate-700 text-xs">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour
        </Button>
        <div className="flex items-center gap-2">
          {isAdmin && isLocked && (
            <Button data-testid="inspection-unlock-button" variant="outline" disabled={unlocking} onClick={handleUnlock} className="border-amber-600 text-amber-400 text-xs">
              <Unlock className="w-4 h-4 mr-1.5" /> Déverrouiller
            </Button>
          )}
          {isAdmin && !isLocked && !editForm && (
            <Button data-testid="inspection-start-edit-button" variant="outline" onClick={startEdit} className="border-amber-600 text-amber-400 text-xs">
              Corriger
            </Button>
          )}
          <Button data-testid="inspection-report-print-button" onClick={() => window.print()} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
            <Printer className="w-4 h-4 mr-1.5" /> Imprimer / Exporter en PDF
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#121820] p-6 space-y-3 print:border-black print:bg-white print:text-black">
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-500 print:text-black mb-1">Rapport d'inspection — {i.Inspection_ID}</div>
          <div data-testid="inspection-lock-badge" className={`flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 rounded print:hidden ${isLocked ? "bg-slate-800 text-slate-400" : "bg-amber-500/20 text-amber-400"}`}>
            {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            {isLocked ? "Verrouillée" : "Déverrouillée — modification en cours"}
          </div>
        </div>
        <h1 className="text-2xl font-black uppercase tracking-tight text-slate-50 print:text-black">{front.Front_Libelle}</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={i.Statut_Front_Apres_Validation || i.Proposition_Statut_Apres_Inspection || i.Statut_Front_Avant_Inspection} />
          <span className="text-sm text-slate-400 print:text-black">{fmtDate(i.Date_Inspection, i.Heure_Debut)}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 text-sm">
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-500 print:text-black">Inspecteur</div>
            <div className="text-slate-200 print:text-black font-semibold">{i.Inspecteur_Nom}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-500 print:text-black">Score</div>
            <div className="text-slate-200 print:text-black font-semibold">{i.Score_Conformite}%</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-500 print:text-black">Statut avant</div>
            <div className="text-slate-200 print:text-black font-semibold">{i.Statut_Front_Avant_Inspection}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-500 print:text-black">Verdict</div>
            <div className="text-slate-200 print:text-black font-semibold">{i.Verdict_Exploitation}</div>
          </div>
          {i.Numero_Tir && (
            <div>
              <div className="text-[10px] font-mono uppercase text-slate-500 print:text-black">Numéro de tir</div>
              <div data-testid="report-numero-tir" className="text-slate-200 print:text-black font-semibold">{i.Numero_Tir}</div>
            </div>
          )}
        </div>
        {i.Commentaires_Generaux && <div className="text-sm text-slate-300 print:text-black pt-2"><b>Commentaire général :</b> {i.Commentaires_Generaux}</div>}
      </div>

      {editForm && (
        <div data-testid="inspection-edit-form" className="rounded-xl border border-amber-600/50 bg-[#1a1610] p-6 space-y-3 print:hidden">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-amber-400">Correction (Administrateur)</div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Commentaire général</label>
            <Textarea data-testid="inspection-edit-commentaire" value={editForm.commentaire} onChange={(e) => setEditForm({ ...editForm, commentaire: e.target.value })} className="bg-slate-900 border-slate-700 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Numéro de tir</label>
            <Input data-testid="inspection-edit-numero-tir" value={editForm.numero_tir} onChange={(e) => setEditForm({ ...editForm, numero_tir: e.target.value })} className="bg-slate-900 border-slate-700 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Personne informée</label>
            <Input data-testid="inspection-edit-personne-informee" value={editForm.personne_informee} onChange={(e) => setEditForm({ ...editForm, personne_informee: e.target.value })} className="bg-slate-900 border-slate-700 text-sm" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button data-testid="inspection-save-edit-button" disabled={saving} onClick={handleSave} className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs">
              Enregistrer (re-verrouille)
            </Button>
            <Button data-testid="inspection-cancel-edit-button" variant="outline" onClick={() => setEditForm(null)} className="border-slate-700 text-xs">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {sections.map((section) => (
        <div key={section} className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-2 print:border-black print:bg-white print:text-black break-inside-avoid">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 print:text-black">{section}</h3>
          {reponses.filter((r) => r.Section === section).map((r) => (
            <div key={r.Reponse_ID} data-testid={`report-reponse-${r.Reponse_ID}`} className="flex items-start justify-between gap-3 text-sm border-t border-slate-800/60 print:border-slate-300 pt-2 first:border-t-0 first:pt-0">
              <span className="text-slate-300 print:text-black flex-1">{r.Question}</span>
              <span className={`font-bold whitespace-nowrap ${r.Anomalie === "TRUE" ? "text-red-400" : "text-emerald-400"} print:text-black`}>{r.Reponse}</span>
            </div>
          ))}
        </div>
      ))}

      {anomalies.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-2 print:border-black print:bg-white print:text-black break-inside-avoid">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 print:text-black">Anomalies détectées</h3>
          {anomalies.map((a) => (
            <div key={a.Anomalie_ID} data-testid={`report-anomalie-${a.Anomalie_ID}`} className="text-sm text-slate-300 print:text-black border-t border-slate-800/60 print:border-slate-300 pt-2 first:border-t-0 first:pt-0">
              <span className="font-bold text-red-400 print:text-black">[{a.Gravite}]</span> {a.Description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
